import "./EnterpriseOrgModal.scss";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { colorOptions } from "../../../../utils/colors";
import ColorPicker from "../../../ColorPicker/ColorPicker";
import useAuthStore from "../../../../store/authStore";
import { createEnterpriseOrganization } from "../../../../api/organizations/organizations";

function EnterpriseOrgModal({ onClose }) {
  const { t } = useTranslation();
  const { token, fetchOrganizations, refreshUserData } = useAuthStore();
  const [organizationName, setOrganizationName] = useState("");
  const [organizationColor, setOrganizationColor] = useState(colorOptions[0]);
  const [billingContactName, setBillingContactName] = useState("");
  const [billingContactEmail, setBillingContactEmail] = useState("");
  const [billingContactPhone, setBillingContactPhone] = useState("");
  const [billingContactCompany, setBillingContactCompany] = useState("");
  const [showError, setShowError] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    // Validation
    if (!organizationName.trim()) {
      setShowError(t("organizations.enterprise.nameRequired"));
      return;
    }

    if (!billingContactName.trim() || !billingContactEmail.trim()) {
      setShowError(t("organizations.enterprise.contactRequired"));
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(billingContactEmail)) {
      setShowError(t("organizations.enterprise.invalidEmail"));
      return;
    }

    setIsCreating(true);
    setShowError("");

    try {
      const orgData = {
        name: organizationName.trim(),
        color: organizationColor,
        billingContact: {
          name: billingContactName.trim(),
          email: billingContactEmail.trim(),
          phone: billingContactPhone.trim(),
          company: billingContactCompany.trim(),
        },
        billingCycle: "monthly",
      };

      await createEnterpriseOrganization(orgData, token);

      // Refresh data
      await fetchOrganizations();
      await refreshUserData();

      // Close modal
      onClose();
    } catch (error) {
      console.error("Failed to create enterprise organization:", error);
      setShowError(error.message || t("organizations.enterprise.createFailed"));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="enterprise-org-modal-overlay" onClick={onClose}>
      <div
        className="enterprise-org-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>{t("organizations.createEnterprise")}</h2>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="info-banner">
            <Trans
              i18nKey="organizations.enterprise.info"
              components={{ strong: <strong /> }}
            />
          </div>

          {showError && <div className="error-banner">{showError}</div>}

          <div className="form-section">
            <label>{t("organizations.enterprise.nameLabel")}</label>
            <input
              type="text"
              className="form-input"
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              placeholder={t("organizations.enterprise.namePlaceholder")}
            />
          </div>

          <div className="form-section">
            <label>{t("organizations.enterprise.colorLabel")}</label>
            <ColorPicker
              selectedColor={organizationColor}
              onColorSelect={setOrganizationColor}
            />
          </div>

          <div className="form-section">
            <h3>{t("organizations.enterprise.billingContact")}</h3>
            <label>{t("organizations.enterprise.contactName")}</label>
            <input
              type="text"
              className="form-input"
              value={billingContactName}
              onChange={(e) => setBillingContactName(e.target.value)}
              placeholder={t("organizations.enterprise.contactNamePlaceholder")}
            />
          </div>

          <div className="form-section">
            <label>{t("organizations.enterprise.contactEmail")}</label>
            <input
              type="email"
              className="form-input"
              value={billingContactEmail}
              onChange={(e) => setBillingContactEmail(e.target.value)}
              placeholder="billing@company.com"
            />
          </div>

          <div className="form-section">
            <label>{t("organizations.enterprise.contactPhone")}</label>
            <input
              type="tel"
              className="form-input"
              value={billingContactPhone}
              onChange={(e) => setBillingContactPhone(e.target.value)}
              placeholder={t("organizations.enterprise.phonePlaceholder")}
            />
          </div>

          <div className="form-section">
            <label>{t("organizations.enterprise.companyName")}</label>
            <input
              type="text"
              className="form-input"
              value={billingContactCompany}
              onChange={(e) => setBillingContactCompany(e.target.value)}
              placeholder={t("organizations.enterprise.companyPlaceholder")}
            />
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="cancel-button"
            onClick={onClose}
            disabled={isCreating}
          >
            {t("common.cancel")}
          </button>
          <button
            className="create-button"
            onClick={handleCreate}
            disabled={isCreating}
          >
            {isCreating
              ? t("common.creating")
              : t("organizations.createEnterprise")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EnterpriseOrgModal;
